import { useEffect, useMemo, useState } from "react";

import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LicenceOrganisationSpecialisation } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationSpecialisation";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TitleAndSpecialtySelection } from "@/Common/Components/User/TitleAndSpecialtySelection";
import { usePartialState } from "@/Common/CustomHooks/usePartialState";
import { useFilteredOrganizationRules } from "@/Common/CustomHooks/User/useFilteredOrganizationRules";
import { useSelectedOrganizationIdentifier } from "@/Common/CustomHooks/User/useSelectedOrganizationIdentifier";
import { useSpecialtyOptionsForLicenceOrganizationRule } from "@/Common/CustomHooks/User/useSpecialtyOptionsForLicenceOrganizationRule";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MedfarDictionariesValidationRuleTypeEnum } from "@/Common/Models/MedfarDictionaries/Enums/MedfarDictionariesValidationRuleTypeEnum";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { DirectoryHealthCareProviderSearchDuplicateDto } from "@/ContactDirectory/Api/ClientParameters/DirectoryHealthCareProviderSearchDuplicateDto";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { MedfarDictionariesValidationHelper } from "@/ContactDirectory/Helpers/MedfarDictionariesValidationHelper";
import { useDefaultDirectoryCountryAndState } from "@/ContactDirectory/Hooks/useDefaultDirectoryCountryAndState";
import { useMedfarDictionariesValidationRule } from "@/ContactDirectory/Hooks/useMedfarDictionariesValidationRule";
import { useOrganizationSectionEntity } from "@/ContactDirectory/Hooks/useOrganizationSectionEntity";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models//DirectoryFaxNumber";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models//DirectoryHealthCareProvider";
import { DirectoryOtherEstablishment } from "@/ContactDirectory/Models//DirectoryOtherEstablishment";
import { DuplicateSearchType } from "@/ContactDirectory/Models/enums/DuplicateSearchType";
import { DirectoryHealthCareProviderValidator } from "@/ContactDirectory/Validators/DirectoryHealthCareProviderValidator";
import { DirectoryContactNumber } from "../Common/DirectoryContactNumber";
import { DirectoryHeader } from "../Common/DirectoryHeader";
import { DisplacedDirectoryFormRow } from "../Common/DisplacedDirectoryFormRow";
import { DirectoryPopupController } from "../DirectoryPopupController";
import { DuplicateWarningPopup } from "../DuplicatePopup/DuplicateWarningPopup";
import { IDirectoryFormProps } from "../IDirectoryFormProps";
import { FaxDirectorySearchResultItem } from "../Search/FaxDirectorySearchResultItem";
import { HealthcareProviderEstablishment } from "./HealthcareProviderEstablishment";

import commonStyles from "../FaxDirectoryForm.module.scss";

export function HealthcareProviderTypeForm(props: IDirectoryFormProps<DirectoryHealthCareProvider, DirectoryHealthCareProvider>): JSX.Element {
    const { getText } = useTranslation();
    const { editModel } = props;
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [editedEstablishmentId, setEditedEstablishmentId] = useState<string | null>(null);
    const [showAddDirectoryPopup, setShowAddDirectoryPopup] = useState(false);
    const { defaultCountry } = useDefaultDirectoryCountryAndState();
    const [entity, setEntityState] = usePartialState<DirectoryHealthCareProvider>(
        () => {
            let linkedEstablishments = DirectoryHealthCareProvider.toDisplayMultipleLinkedEstablishments(props.editModel?.linkedEstablishments);
            if (!linkedEstablishments?.any())
                linkedEstablishments = [new DirectoryOtherEstablishment()];

            if (props.editModel) {
                const editEntity = new DirectoryHealthCareProvider({
                    ...props.editModel,
                    linkedEstablishments: linkedEstablishments,
                    linkedFaxNumbers: linkedEstablishments.flatMap(p => p.faxNumbers)
                });
                if (!editEntity.faxNumber.countryId) {
                    editEntity.faxNumber.countryId ??= defaultCountry.Id;
                }
                return editEntity;
            }
            return new DirectoryHealthCareProvider({
                linkedEstablishments: [new DirectoryOtherEstablishment()],
                linkedFaxNumbers: [new DirectoryFaxNumber()],
                faxNumber: new DirectoryFaxNumber({ countryId: defaultCountry.Id })
            });
        },
        s => new DirectoryHealthCareProvider(s)
    );

    function setEntity(updatedFields: Partial<DirectoryHealthCareProvider>): void {
        const newState = setEntityState(updatedFields);
        props.onModelUpdate(newState);
    }

    function setFax(faxNumber: Partial<DirectoryFaxNumber>): void {
        setEntity({ faxNumber: new DirectoryFaxNumber({ ...entity.faxNumber, ...faxNumber }) });
    }

    const title = useMemo<LicenceOrganisationRule | null>(
        () => IsNullOrWhiteSpace(entity.licenseOrganizationRuleId)
            ? null
            : cacheStore.AllLicenseOrganizationRulesById[entity.licenseOrganizationRuleId],
        [entity.licenseOrganizationRuleId]
    );

    const specialty = useMemo<LicenceOrganisationSpecialisation | null>(
        () => IsNullOrWhiteSpace(entity.licenseOrganizationSpecializationId)
            ? null
            : cacheStore.TitlesAndSpecialtiesCache.map(p => p.Model.licenceOrganisationSpecialisationModel).find(p => p.Id === entity.licenseOrganizationSpecializationId),
        [entity.licenseOrganizationSpecializationId]
    );

    const validator = useMemo(
        () => new DirectoryHealthCareProviderValidator(
            new MedfarDictionariesValidationHelper(cacheStore.MedfarDictionariesValidationRules),
            cacheStore.CountryCache
        ),
        []
    );

    const specialtyOptions = useSpecialtyOptionsForLicenceOrganizationRule(title);

    const validationResult = useMemo(
        () => validator.validate(entity, specialtyOptions),
        [validator, entity]
    );

    const filteredOrganizationRules = useFilteredOrganizationRules(validationResult.isEmployeeTitleValid ? title : null, null);

    const [selectedOrganizationIdentifier, setSelectedOrganizationIdentifier] = useState<string>(
        useSelectedOrganizationIdentifier(filteredOrganizationRules, title, true)
    );

    const validationRulePhone = useMedfarDictionariesValidationRule(MedfarDictionariesValidationRuleTypeEnum.PhoneNumber, entity.faxNumber.countryId);

    // Parent updates
    useEffect(() => props.onValidityChange(validationResult.isFormValid && !isDuplicate), [validationResult.isFormValid, isDuplicate]);

    useEffect(
        () => {
            setIsDuplicate(false);

            if (IsNullOrWhiteSpace(title?.Id) || IsNullOrWhiteSpace(entity.licenseNumber))
                return;

            const timeout = setTimeout(
                async () => setIsDuplicate(
                    await props.checkIfDisallowedDuplicate({
                        licenseOrganizationRuleId: title.Id,
                        licenseNumber: entity.licenseNumber,
                        exceptHealthCareProviderId: entity.id
                    })
                ),
                500
            );

            return () => clearTimeout(timeout);
        },
        [entity.id, entity.licenseNumber, title]
    );

    const countries = useMemo(
        () => cacheStore.GetGeographicCountriesByIsoCodes(["CA", "US"]),
        []
    );

    const countryOptions = useMemo(
        () => countries.map(p => new KeyValuePair(p.Id, p.Name)),
        [countries]
    );
    const onSelectEntity = useOrganizationSectionEntity(entity, {
        setEditedEstablishmentId,
        setEntity,
        setShowAddDirectoryPopup
    });

    const showAddSearchEstablishmentField = useMemo(() => entity.linkedEstablishments?.all(p => !IsNullOrWhiteSpace(p.name)), [entity.linkedEstablishments]);

    const showInformationFromProfessionalOrganisationSection = useMemo(() => props.editModel && (!IsNullOrWhiteSpace(props.editModel?.rawAddress) || !IsNullOrWhiteSpace(props.editModel?.rawPhoneNumber)), []);

    return (
        <div>
            {props.showWarningDuplicateMessage && (
                <DuplicateWarningPopup
                    {...props.duplicateWarningPopupProps}
                    onModify={entity => {
                        setEntity(entity);
                        props.duplicateWarningPopupProps.onModify(entity);
                        props.duplicateWarningPopupProps.onClose();
                    }}
                    onUsedInstead={entity => {
                        props.onEntityUseInstead(entity);
                        props.duplicateWarningPopupProps.onClose();
                    }}
                />
            )}
            <DisplacedDirectoryFormRow
                labelKey="FirstNameLabel"
                className={commonStyles.removeableRightSpacing}
            >
                <TextboxControl
                    valid={validationResult.firstName}
                    value={entity.firstName}
                    onChange={firstName => setEntity({ firstName })}
                    maxLength={250}
                />
            </DisplacedDirectoryFormRow>
            <DisplacedDirectoryFormRow
                labelKey="LastNameLabel"
                className={commonStyles.removeableRightSpacing}
            >
                <TextboxControl
                    valid={validationResult.lastName}
                    value={entity.lastName}
                    onChange={lastName => setEntity({ lastName })}
                    maxLength={250}
                />
            </DisplacedDirectoryFormRow>
            <TitleAndSpecialtySelection
                filteredOrganizationRules={filteredOrganizationRules}
                selectedOrganizationIdentifier={selectedOrganizationIdentifier}
                title={validationResult.isEmployeeTitleValid ? title : null}
                specialty={specialty}
                onOrganizationSelect={organization => {
                    setEntity({
                        licenseOrganizationRuleId: "",
                        licenseOrganizationSpecializationId: ""
                    });
                    setSelectedOrganizationIdentifier(organization);
                }}
                onTitleSelect={title => {
                    const licenseOrganizationRuleId = title?.Id ?? "";
                    setEntity({
                        licenseOrganizationRuleId,
                        licenseOrganizationSpecializationId: null
                    });

                    if (!IsNullOrWhiteSpace(licenseOrganizationRuleId) && !IsNullOrWhiteSpace(entity.licenseNumber) && validationResult.licenseNumber) {
                        props.searchDuplicateForWarning(
                            entity,
                            new DirectoryHealthCareProviderSearchDuplicateDto({ licenseOrganizationRuleId, licenseNumber: entity.licenseNumber }),
                            DuplicateSearchType.LicenceNumber
                        );
                    }
                }}
                onSpecialtySelect={specialty => setEntity({ licenseOrganizationSpecializationId: specialty?.Id ?? "" })}
                renderItemContainer={(child, index) => {
                    switch (index) {
                        case 0:
                            return <DisplacedDirectoryFormRow labelKey="ProfessionalOrganizationLabel" className={commonStyles.removeableRightSpacing}>{child}</DisplacedDirectoryFormRow>;
                        case 1:
                            return <DisplacedDirectoryFormRow labelKey="TitleLabel" className={commonStyles.removeableRightSpacing}>{child}</DisplacedDirectoryFormRow>;
                        case 2:
                            return <DisplacedDirectoryFormRow labelKey="SpecialtyLabel" className={commonStyles.removeableRightSpacing}>{child}</DisplacedDirectoryFormRow>;
                    }
                }}
                isTitleRequired
                isSpecialtyRequired
                isOrganisationValid={!!selectedOrganizationIdentifier}
                isTitleValid={validationResult.licenseOrganizationRuleId && validationResult.isEmployeeTitleValid}
                isSpecialtyValid={validationResult.licenseOrganizationSpecializationId}
            />
            <DisplacedDirectoryFormRow
                labelKey="LicenseNumberLabel"
                className={commonStyles.removeableRightSpacing}
            >
                <TextboxControl
                    valid={validationResult.licenseNumber && !isDuplicate}
                    value={entity.licenseNumber}
                    onChange={value => setEntity({ licenseNumber: value })}
                    onBlur={() => {
                        if (!IsNullOrWhiteSpace(entity.licenseNumber) && validationResult.licenseNumber && validationResult.licenseOrganizationRuleId) {
                            props.searchDuplicateForWarning(
                                entity,
                                new DirectoryHealthCareProviderSearchDuplicateDto({ licenseOrganizationRuleId: entity.licenseOrganizationRuleId, licenseNumber: entity.licenseNumber }),
                                DuplicateSearchType.LicenceNumber
                            );
                        }
                    }}
                    maxLength={50}
                />
                {isDuplicate && (
                    <div className="red-text margin-xs-top">
                        {`${getText("FieldCannotBeReusedInTwoEntriesLabel", { field: getText("TheLicenseNumberLabel") })} ${getText("withinTheSameOrganizationLabel")}`}
                    </div>
                )}
            </DisplacedDirectoryFormRow>
            {showInformationFromProfessionalOrganisationSection && !entity.establishmentLinkedToRawInformationId && (
                <div className="margin-sm-bottom">
                    <DirectoryHeader
                        labelKey="InformationFromProfessionalOrganizationLabel"
                        className={commonStyles.removeableRightSpacing}
                        shouldDisplayEditLabel={cacheStore.ClinicSettings.EnableDirectoryOrganizationEdit}
                        onEdit={() => setShowAddDirectoryPopup(true)}
                    />
                    {!IsNullOrWhiteSpace(editModel.rawAddress) && <p>{editModel.rawAddress}</p>}
                    {!IsNullOrWhiteSpace(editModel.rawPhoneNumber) && <p>T: {editModel.rawPhoneNumber}</p>}
                </div>
            )}
            <DirectoryHeader
                labelKey="FaxNumberLabel"
                className={commonStyles.removeableRightSpacing}
            />
            <div className={commonStyles.removeableRightSpacing}>
                <DirectoryContactNumber
                    id="faxNumber"
                    number={entity.faxNumber?.number}
                    isFax={true}
                    onNumberChange={number => setFax({ number })}
                    countryOptions={countryOptions}
                    selectedCountryId={entity.faxNumber?.countryId}
                    onSelectCountryId={countryId => { setFax({ countryId }); }}
                    isValid={validationResult.faxNumber}
                    validationRule={validationRulePhone}
                />
            </div>
            {/* Linked Establishments */}

            <DirectoryHeader
                labelKey="LinkedEstablishmentsLabel"
                onAdd={showAddSearchEstablishmentField ? () => setEntity({ linkedEstablishments: [...entity.linkedEstablishments, new DirectoryOtherEstablishment()] }) : null}
                className={commonStyles.removeableRightSpacing}
            />

            {entity.linkedEstablishments?.map((p, i) => (
                <HealthcareProviderEstablishment
                    key={`linkedEstablishment-${p.id}-${p.selectedLinkedFaxNumber?.id ?? p.faxNumbers.firstOrDefault()?.id}`}
                    establishment={p}
                    faxNumber={p.selectedLinkedFaxNumber ?? p.faxNumbers.firstOrDefault()}
                    onSelect={(establishment, fax) => {
                        entity.linkedEstablishments[i] = establishment;
                        if (fax?.id) {
                            entity.linkedFaxNumbers[i] = fax;
                            entity.linkedEstablishments[i].selectedLinkedFaxNumber = fax;
                        }

                        setEntity({
                            linkedEstablishments: [...entity.linkedEstablishments],
                            linkedFaxNumbers: [...entity.linkedFaxNumbers]
                        });
                    }}
                    onRemove={(faxId, establishmentId) => {
                        const isAddedFromOrganizationSection = establishmentId === editedEstablishmentId;
                        const shouldPrefillBack = isAddedFromOrganizationSection && !!editedEstablishmentId;

                        let linkedEstablishments = [...entity.linkedEstablishments];
                        linkedEstablishments.splice(i, 1);
                        const filteredLinkedFaxNumbers = entity.linkedFaxNumbers.filter(p => p.id != faxId);

                        setEntity({
                            linkedEstablishments: linkedEstablishments?.any()
                                ? linkedEstablishments
                                : [new DirectoryOtherEstablishment()],
                            linkedFaxNumbers: filteredLinkedFaxNumbers,
                            establishmentLinkedToRawInformationId: isAddedFromOrganizationSection ? null : entity.establishmentLinkedToRawInformationId,
                            faxNumber: shouldPrefillBack ? new DirectoryFaxNumber(props?.editModel.faxNumber) : entity.faxNumber
                        });
                        if (isAddedFromOrganizationSection) {
                            setEditedEstablishmentId(null);
                        }
                    }}
                    isValid={validationResult.linkedEstablishments}
                    linkedFaxNumbers={{
                        selectedLinkedFaxNumbers: [...entity.linkedFaxNumbers],
                        entityFaxNumber: entity.faxNumber
                    }}
                    shouldHideRemoveButton={IsNullOrWhiteSpace(entity.linkedEstablishments?.firstOrDefault()?.id)}
                />
            ))}

            {showAddDirectoryPopup && (
                <DirectoryPopupController
                    onSave={other => onSelectEntity(new FaxDirectorySearchResultItem({ entity: other }))}
                    onClose={() => setShowAddDirectoryPopup(false)}
                    rawInformation={{
                        rawAddress: entity.rawAddress,
                        rawPhoneNumber: entity.rawPhoneNumber,
                        faxNumber: entity.faxNumber
                    }}
                    directoryTypeSelectionOptions={[DirectoryEntityType.Other]}
                    onEntityUseInstead={other => onSelectEntity(new FaxDirectorySearchResultItem({ entity: other }))}
                    onModify={other => onSelectEntity(new FaxDirectorySearchResultItem({ entity: other }))}
                />
            )}
        </div>
    );
}
